// src/lib/financeiro/metricasFaturamento.ts
//
// Métricas do Dashboard do Faturamento: tempo médio de faturamento, boletos
// emitidos fora do prazo padrão e ranking de motivos.
//
// O que conta como "um faturamento" é a PRIMEIRA emissão de uma fatura — a mesma
// definição que dispensa reemissão e boleto avulso do modal de justificativa
// (src/lib/janelaFaturamento.ts). Sem isso o mesmo trabalho seria contado duas
// vezes toda vez que alguém cancelasse e reemitisse.

import { diffInDays } from '../janelaCobranca.ts';
import { ehBoletoAvulso, exigeJustificativaAtraso } from '../janelaFaturamento.ts';

/** Uma emissão individual, já achatada a partir de `faturas.historico_emissoes`. */
export interface EmissaoFaturamento {
  faturaId: string;
  ucCodigo?: string | null;
  clienteNome?: string | null;
  mesReferencia?: string | null;
  /** Quem emitiu, como gravado no histórico. */
  emitidoPorNome: string;
  /**
   * Instante da emissão. Prefira `registrado_em` (now() do servidor) a
   * `emitido_em` (relógio do navegador de quem clicou, que já se provou
   * divergente em minutos entre máquinas).
   */
  emitidoEm: string;
  /**
   * Quando a fatura da distribuidora ficou disponível no sistema. Hoje é o
   * `faturas.created_at`: a etapa "Fatura disponível" é representada por
   * `status_fatura IS NULL` e não tem carimbo próprio, então a criação da linha
   * é o instante mais próximo que existe.
   */
  disponivelEm?: string | null;
  /** `faturas.data_vencimento` — o vencimento da fatura da Equatorial. */
  dataVencimento?: string | null;
  classificacao?: string | null;
  /** Posição no `historico_emissoes`. 0 = primeira emissão; >0 = reemissão. */
  ordem: number;
  motivo?: string | null;
  justificativa?: string | null;
}

export interface MetricasUsuario {
  usuario: string;
  /** Primeiras emissões atribuídas a esta pessoa no período. */
  totalEmitidos: number;
  foraDoPrazo: number;
  /** `foraDoPrazo / totalEmitidos`. `null` quando não emitiu nada. */
  taxaForaDoPrazo: number | null;
  /** Média de dias entre a fatura ficar disponível e o boleto sair. */
  tempoMedioDias: number | null;
  /** Quantas emissões tinham data de disponibilidade — o denominador do tempo médio. */
  comTempoMedido: number;
}

export interface ItemRankingMotivo {
  motivo: string;
  qtde: number;
  /** Fração do total de emissões fora do prazo COM motivo registrado. */
  pct: number;
}

export interface MetricasFaturamento {
  /** O time somado — "Faturamento em conjunto". */
  departamento: MetricasUsuario;
  /** Uma linha por pessoa, da maior para a menor taxa de atraso. */
  porUsuario: MetricasUsuario[];
  rankingMotivos: ItemRankingMotivo[];
  /**
   * As emissões que estouraram o prazo, para a tela poder mostrar QUAIS faturas
   * estão por trás do número — da mais atrasada para a menos. Já respeita todos
   * os recortes aplicados (período, pessoa, time).
   */
  emissoesForaDoPrazo: EmissaoFaturamento[];
  /** Emissões fora do prazo que ficaram sem motivo (anteriores à regra, por ex.). */
  foraDoPrazoSemMotivo: number;
  /**
   * Emissões com atraso acima de `DIAS_MAX_ATRASO_CONSIDERADO`, deixadas de fora
   * da conta de atraso. Contam como trabalho feito no total de faturados.
   */
  acimaDoCorte: number;
  reemissoesIgnoradas: number;
  avulsosIgnorados: number;
}

const vazio = (usuario: string): MetricasUsuario => ({
  usuario,
  totalEmitidos: 0,
  foraDoPrazo: 0,
  taxaForaDoPrazo: null,
  tempoMedioDias: null,
  comTempoMedido: 0,
});

/**
 * `true` quando esta emissão conta como um faturamento para as métricas:
 * é a primeira emissão da fatura e não é boleto avulso.
 */
export const contaComoFaturamento = (e: EmissaoFaturamento): boolean =>
  e.ordem === 0 && !ehBoletoAvulso(e.classificacao);

/**
 * Acima deste atraso a emissão sai da conta.
 *
 * Um atraso de meses não descreve o ritmo do faturamento: é fatura antiga lançada
 * retroativamente, dívida renegociada ou acerto de histórico — trabalho que existe,
 * mas que não é o boleto do mês saindo no prazo. Sem o corte esses casos dominam a
 * média e a taxa: nos últimos 30 dias eram 46 "atrasos", dos quais 45 tinham mais
 * de 45 dias e vinham de cargas retroativas.
 *
 * Vale só para a MÉTRICA. O modal de justificativa continua pedindo motivo em
 * qualquer atraso, por maior que seja.
 */
export const DIAS_MAX_ATRASO_CONSIDERADO = 45;

/**
 * `true` quando a emissão saiu depois do prazo padrão — mesmo limiar do modal de
 * justificativa (`data_vencimento` menos 7 dias corridos / 5 dias úteis) —, dentro
 * do corte de `DIAS_MAX_ATRASO_CONSIDERADO`.
 */
export const emissaoForaDoPrazo = (e: EmissaoFaturamento): boolean => {
  const emissao = new Date(e.emitidoEm);
  if (isNaN(emissao.getTime())) return false;
  const r = exigeJustificativaAtraso({
    dataVencimento: e.dataVencimento,
    dataEmissao: emissao,
    classificacao: e.classificacao,
    // A exceção de reemissão já foi aplicada por `contaComoFaturamento`; aqui a
    // pergunta é só sobre a data, então o contexto entra limpo de propósito.
  });
  return r.exige && r.diasDeAtraso <= DIAS_MAX_ATRASO_CONSIDERADO;
};

/**
 * Quantos dias a emissão passou do prazo padrão. 0 quando está no prazo (ou
 * quando não dá para saber). Mesma conta que decide `emissaoForaDoPrazo`.
 */
export const diasDeAtrasoDaEmissao = (e: EmissaoFaturamento): number => {
  const emissao = new Date(e.emitidoEm);
  if (isNaN(emissao.getTime())) return 0;
  const r = exigeJustificativaAtraso({
    dataVencimento: e.dataVencimento,
    dataEmissao: emissao,
    classificacao: e.classificacao,
  });
  return r.exige ? r.diasDeAtraso : 0;
};

/** Dias entre a fatura ficar disponível e o boleto ser emitido. `null` sem disponibilidade. */
export const tempoDeFaturamentoEmDias = (e: EmissaoFaturamento): number | null => {
  if (!e.disponivelEm) return null;
  const dias = diffInDays(e.emitidoEm, e.disponivelEm);
  if (dias === null) return null;
  // Emissão anterior à criação da linha não existe no mundo real; é dado sujo
  // (importação, ajuste manual) e distorceria a média para baixo.
  return dias < 0 ? null : dias;
};

const consolidar = (usuario: string, emissoes: EmissaoFaturamento[]): MetricasUsuario => {
  const total = emissoes.length;
  const fora = emissoes.filter(emissaoForaDoPrazo).length;
  const tempos = emissoes
    .map(tempoDeFaturamentoEmDias)
    .filter((d): d is number => d !== null);

  return {
    usuario,
    totalEmitidos: total,
    foraDoPrazo: fora,
    taxaForaDoPrazo: total > 0 ? fora / total : null,
    tempoMedioDias: tempos.length > 0 ? tempos.reduce((a, b) => a + b, 0) / tempos.length : null,
    comTempoMedido: tempos.length,
  };
};

/**
 * Consolida as métricas do dashboard.
 *
 * `usuariosDoFaturamento`, quando informado, restringe o cálculo às pessoas do
 * departamento — as demais são descartadas em vez de aparecerem no ranking.
 */
export const calcularMetricasFaturamento = (
  emissoes: EmissaoFaturamento[],
  usuariosDoFaturamento?: string[] | null,
): MetricasFaturamento => {
  const permitido = usuariosDoFaturamento && usuariosDoFaturamento.length > 0
    ? new Set(usuariosDoFaturamento.map((u) => u.trim().toLowerCase()))
    : null;

  const reemissoesIgnoradas = emissoes.filter((e) => e.ordem !== 0).length;
  const avulsosIgnorados = emissoes.filter((e) => e.ordem === 0 && ehBoletoAvulso(e.classificacao)).length;

  const consideradas = emissoes
    .filter(contaComoFaturamento)
    .filter((e) => !permitido || permitido.has((e.emitidoPorNome || '').trim().toLowerCase()));

  const porNome = new Map<string, EmissaoFaturamento[]>();
  consideradas.forEach((e) => {
    const nome = (e.emitidoPorNome || 'Sem atribuição').trim() || 'Sem atribuição';
    if (!porNome.has(nome)) porNome.set(nome, []);
    porNome.get(nome)!.push(e);
  });

  const porUsuario = Array.from(porNome.entries())
    .map(([nome, lista]) => consolidar(nome, lista))
    // Maior taxa primeiro; empate desempata por volume, para a linha de cima ser
    // sempre a que mais pesa e não a que por acaso veio antes no alfabeto.
    .sort((a, b) => (b.taxaForaDoPrazo ?? -1) - (a.taxaForaDoPrazo ?? -1) || b.totalEmitidos - a.totalEmitidos);

  const fora = consideradas.filter(emissaoForaDoPrazo);
  const comMotivo = fora.filter((e) => (e.motivo || '').trim());

  const contagem = new Map<string, number>();
  comMotivo.forEach((e) => {
    const m = e.motivo!.trim();
    contagem.set(m, (contagem.get(m) || 0) + 1);
  });

  const rankingMotivos: ItemRankingMotivo[] = Array.from(contagem.entries())
    .map(([motivo, qtde]) => ({ motivo, qtde, pct: comMotivo.length > 0 ? qtde / comMotivo.length : 0 }))
    .sort((a, b) => b.qtde - a.qtde || a.motivo.localeCompare(b.motivo, 'pt-BR'));

  // Mais atrasada primeiro: é a que interessa olhar. `diasDeAtrasoDaEmissao`
  // devolve 0 para o que não atrasou, e `fora` já só tem atrasadas.
  const emissoesForaDoPrazo = fora
    .slice()
    .sort((a, b) => diasDeAtrasoDaEmissao(b) - diasDeAtrasoDaEmissao(a));

  return {
    departamento: consideradas.length > 0
      ? consolidar('Faturamento (conjunto)', consideradas)
      : vazio('Faturamento (conjunto)'),
    porUsuario,
    rankingMotivos,
    emissoesForaDoPrazo,
    acimaDoCorte: consideradas.filter((e) => diasDeAtrasoDaEmissao(e) > DIAS_MAX_ATRASO_CONSIDERADO).length,
    foraDoPrazoSemMotivo: fora.length - comMotivo.length,
    reemissoesIgnoradas,
    avulsosIgnorados,
  };
};
